import { useMemo, useState } from 'react'
import { Icon } from '@carrent/ui'

/**
 * Таблица консоли.
 *
 * ── Почему здесь, а не в @carrent/ui ──────────────────────────────────
 *
 * Стили таблиц живут в console.css, и это не случайность: класс
 * table-wrap внесён в forbiddenClasses водительского бандла в
 * scripts/check-isolation.mjs. Перенести стили в base.css нельзя —
 * его импортируют оба приложения, и проверка изоляции упадёт. Держать
 * компонент в общем пакете, а его стили в приложении, значит развести
 * две половины одного целого по разным местам.
 *
 * У водителя таблиц нет ни одной: мобильный интерфейс строится на
 * карточках и шагах. «Общий» примитив был бы общим только на бумаге.
 *
 * Прецедент уже есть: RegionPicker и TransferCarModal переехали сюда же
 * из @carrent/ui по той же причине — через баррель они тянули в
 * водительский бандл лишнее.
 *
 * ── Что примитив забирает у страниц ───────────────────────────────────
 *
 * Четыре страницы независимо писали <table> руками, и именно поэтому
 * четыре из них разъехались с дизайн-системой, когда исчез global.css.
 * Здесь собрано то, что каждая писала заново и каждая по-своему:
 * сортировка, состояния загрузки/ошибки/пустоты и — чего не было нигде,
 * кроме задач, — доступ с клавиатуры. Кликабельная строка без tabIndex
 * недостижима без мыши, а таблица это основной способ навигации
 * по консоли.
 *
 * ── Описание колонки ──────────────────────────────────────────────────
 *
 *   key       — идентификатор, идёт в className и в состояние сортировки
 *   label     — заголовок
 *   render    — (row) => ReactNode; по умолчанию row[key]
 *   sortable  — включает кнопку сортировки в шапке
 *   sortValue — (row) => сравнимое значение; по умолчанию row[key]
 *   width     — суффикс класса table__col--<width> из console.css
 */

function compare(a, b) {
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return String(a).localeCompare(String(b), 'ru')
}

export function DataTable({
  columns,
  rows,
  rowKey = (row) => row.id,
  onRowClick,
  rowClassName,
  loading = false,
  error = null,
  onRetry,
  empty = {},
  dense = true,
  skeletonRows = 4,
}) {
  const [sort, setSort] = useState(null)

  const sorted = useMemo(() => {
    if (!sort) return rows
    const column = columns.find((c) => c.key === sort.key)
    if (!column) return rows

    const value = column.sortValue || ((row) => row[column.key])
    const factor = sort.dir === 'asc' ? 1 : -1
    // Копия: сортировка на месте перетасовала бы массив, которым владеет
    // страница, и следующий рендер получил бы уже переставленные строки.
    return [...rows].sort((a, b) => {
      const av = value(a)
      const bv = value(b)
      // Пустые значения всегда внизу, и направление на это не влияет:
      // строка без срока не «самая ранняя» и не «самая поздняя», она
      // просто без срока. Поэтому проверка стоит до умножения на factor.
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      return compare(av, bv) * factor
    })
  }, [rows, sort, columns])

  const toggleSort = (key) =>
    setSort((s) =>
      s?.key === key
        ? s.dir === 'asc'
          ? { key, dir: 'desc' }
          : null // третий клик снимает сортировку и возвращает порядок сервера
        : { key, dir: 'asc' },
    )

  if (loading) {
    return (
      <div className="table-wrap">
        <div className="p-page flex-col gap-sm">
          {Array.from({ length: skeletonRows }, (_, i) => (
            <span key={i} className="skeleton table__skeleton-row" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="state">
        <p className="state__title">{error}</p>
        {onRetry && (
          <button type="button" className="btn btn--secondary" onClick={onRetry}>
            Повторить
          </button>
        )}
      </div>
    )
  }

  if (rows.length === 0) {
    return (
      <div className="state">
        {empty.icon && <Icon name={empty.icon} size={32} />}
        <p className="state__title">{empty.title || 'Ничего не найдено'}</p>
        {empty.text && <p className="state__text">{empty.text}</p>}
        {empty.action}
      </div>
    )
  }

  const activate = (row) => onRowClick && onRowClick(row)

  return (
    <div className="table-wrap">
      <table className={`table ${dense ? 'table--dense' : ''}`.trim()}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                className={c.width ? `table__col--${c.width}` : undefined}
                aria-sort={
                  sort?.key === c.key
                    ? sort.dir === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : 'none'
                }
              >
                {c.sortable ? (
                  <button type="button" className="table__sort" onClick={() => toggleSort(c.key)}>
                    {c.label}
                    {sort?.key === c.key && (
                      <Icon
                        name="chevronDown"
                        size={12}
                        className={
                          sort.dir === 'asc' ? 'table__sort-icon table__sort-icon--asc' : 'table__sort-icon'
                        }
                      />
                    )}
                  </button>
                ) : (
                  c.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => {
            const extra = rowClassName ? rowClassName(row) : ''
            return (
              <tr
                key={rowKey(row)}
                className={`${onRowClick ? 'table__row--clickable' : ''} ${extra}`.trim() || undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onClick={onRowClick ? () => activate(row) : undefined}
                onKeyDown={
                  onRowClick
                    ? (e) => {
                        // Пробел на строке иначе прокручивает страницу:
                        // браузер не знает, что строка ведёт себя как ссылка.
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          activate(row)
                        }
                      }
                    : undefined
                }
              >
                {columns.map((c) => (
                  <td key={c.key} className={c.cellClassName}>
                    {c.render ? c.render(row) : row[c.key]}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
